import React, { useState, useEffect, useRef } from 'react';
import './PricingPage.css';
import api from '../api/apiClient';
import Header from '../components/Header';
import { duracionDeLaPrueba } from "../limites";
import { enlaceWhatsApp } from "../contacto";

// ─── Floating Orbs Background ───────────────────────────────────────────────
function FloatingOrbs() {
  return (
    <div className="prc-orbs" aria-hidden="true">
      <div className="prc-orb prc-orb-1" />
      <div className="prc-orb prc-orb-2" />
      <div className="prc-orb prc-orb-3" />
      <div className="prc-orb prc-orb-4" />
    </div>
  );
}

// ─── Live Social Proof Ticker ───────────────────────────────────────────────
// Los nombres que aparecian aqui —"Premium", "Speed Pack", "Boost Tokens"— ya
// no existen. El aviso quedaba justo encima de la rejilla, asi que el alumno
// leia que alguien acaba de contratar un plan que no puede encontrar debajo.
const PROOF_ITEMS = [
  { name: "Rodrigo M.", city: "CDMX", plan: "Elite", ago: "esta semana" },
  { name: "Valeria G.", city: "Guadalajara", plan: "Jumpstart", ago: "recientemente" },
  { name: "Carlos T.", city: "Monterrey", plan: "Boost", ago: "este mes" },
  { name: "Ana P.", city: "Puebla", plan: "Elite", ago: "recientemente" },
];

function SocialProofTicker() {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % PROOF_ITEMS.length);
        setVisible(true);
      }, 400);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const item = PROOF_ITEMS[idx];
  return (
    <div className={`prc-proof-ticker ${visible ? 'prc-proof-visible' : 'prc-proof-hidden'}`}>
      <span className="prc-proof-dot" />
      <span><strong>{item.name}</strong> de {item.city} acaba de adquirir <strong>{item.plan}</strong> — {item.ago}</span>
    </div>
  );
}

// ─── Plan Card (LPricing ADN · prc- prefix) ───────────────────────────────
function PlanCard({ plan, onSelect, tokensPrevio = 0, cupon = null }) {
  const planColor = plan.color || '#6366f1';
  const isPopular = plan.badge === 'MÁS POPULAR' || (plan.badge || '').includes('POPULAR');
  const isElite = plan.badge === 'ELITE';
  const isFeatured = plan.featured || isPopular;
  // El cupón personal manda sobre la rebaja del catálogo: es el que le
  // prometimos a ESTE alumno, en su chat, con fecha y código.
  const conCupon = cupon && plan.price > 0
    && (!cupon.plan_id || cupon.plan_id === plan.id)
    ? Math.floor(Number(plan.price) * (1 - Number(cupon.pct) / 100))
    : null;
  // Son DOS descuentos distintos y no se pueden mezclar: el del catálogo (una
  // rebaja para todo el mundo, con su etiqueta) y el cupón personal de este
  // alumno. Al juntarlos, la cinta del catálogo salía con «-0%» encima de un
  // precio que sí estaba rebajado por cupón (20-09-2026).
  const descuentoDeCatalogo = !!plan.discount_active
    && Number(plan.original_price) > Number(plan.price)
    && Number(plan.discount_pct) > 0;
  const hasDiscount = conCupon !== null || descuentoDeCatalogo;

  // Los tokens ya se anuncian arriba en grande: repetirlos como vineta era
  // decir dos veces lo mismo en la misma tarjeta.
  // Acepta las dos formas de nombrarlo: lo que se escriba en el CRM puede ir
  // por detras del cambio de nombre, y el resultado de no filtrarlo seria
  // decir la misma cifra dos veces en la misma tarjeta.
  const caracteristicas = (plan.features || [])
    .filter(f => !/(tokens\s+de\s+IA|energ[íi]a\s+spacy)/i.test(f));

  // El multiplicador se lee contra el ESCALON ANTERIOR, no contra el plan de
  // pago mas barato. Comparando con el mas barato, Elite salia "x3" mirando a
  // Boost, y quien esta decidiendo entre Jumpstart y Elite —que es la decision
  // real en esa columna— lee un numero que no le habla de su comparacion.
  const veces = tokensPrevio > 0 ? plan.token_limit / tokensPrevio : 0;
  const multiplicador = veces >= 2 ? `×${Number.isInteger(veces) ? veces : veces.toFixed(1)}` : null;

  const cardCls = [
    'prc-card',
    isPopular ? 'prc-card-popular' : '',
    isElite ? 'prc-card-elite' : '',
    isFeatured && !isPopular && !isElite ? 'prc-card-featured' : '',
  ].filter(Boolean).join(' ');

  const ctaCls = isPopular ? 'prc-cta-popular'
    : isElite ? 'prc-cta-elite'
      : 'prc-cta-default';

  return (
    <div className={cardCls} style={{ '--plan-color': planColor }}>
      {/* Badge top */}
      {plan.badge && (
        <div className={`prc-lp-badge ${isPopular ? 'prc-lp-badge-popular' : isElite ? 'prc-lp-badge-elite' : 'prc-lp-badge-custom'
          }`}>{plan.badge}</div>
      )}

      {/* Discount ribbon */}
      {conCupon !== null ? (
        <div className="prc-lp-ribbon" data-prueba="cinta-cupon">
          🎁 -{Number(cupon.pct).toFixed(0)}% · solo para ti
        </div>
      ) : descuentoDeCatalogo && (
        <div className="prc-lp-ribbon">
          🏷️ -{plan.discount_pct}% · {plan.discount_label}
        </div>
      )}

      {/* Header: emoji inline + name grande */}
      <div className="prc-lp-header">
        {plan.emoji && <span className="prc-lp-emoji">{plan.emoji}</span>}
        <h3 className="prc-lp-name">{plan.name}</h3>
      </div>

      {/* Precio masivo */}
      <div className="prc-lp-price-block">
        {hasDiscount && (
          <div className="prc-lp-original" data-prueba={conCupon !== null ? 'precio-con-cupon' : undefined}>
            <span className="prc-lp-strike">
              ${Number(conCupon !== null ? plan.price : plan.original_price).toLocaleString('es-MX')} {plan.currency}
            </span>
            <span className="prc-lp-disc-pill">
              -{conCupon !== null ? Number(cupon.pct).toFixed(0) : plan.discount_pct}%
            </span>
          </div>
        )}
        {conCupon !== null && (
          <div className="prc-lp-cupon-nota">tu primer pago · después ${Number(plan.price).toLocaleString('es-MX')}</div>
        )}
        <div className="prc-lp-price" style={{ color: hasDiscount ? planColor : undefined }}>
          ${Number(conCupon ?? plan.price ?? 0).toLocaleString('es-MX')}
        </div>
        <div className="prc-lp-period">
          {plan.category === 'package'
            ? `Paquete de ${plan.classes_per_month > 0 ? plan.classes_per_month : '?'} clases`
            : plan.price === 0 ? 'Dos Días' : 'por mes'}
        </div>
      </div>

      {/* ── El combustible, ARRIBA y en grande ───────────────────────────
          Los tokens de IA son LA diferencia entre un plan y otro, asi que van
          antes que nada, no como una vineta perdida al final de la lista ni
          como la letra pequena de debajo del boton. Cuando un plan da mas que
          el de entrada, se dice cuanto mas: "x3" se entiende de un vistazo y
          "6,000" no. El multiplicador se CALCULA con los planes que haya en
          ese momento, no esta escrito a mano: si cambias los tokens en el CRM,
          la comparacion se corrige sola. */}
      {plan.token_limit > 0 && (
        <div className="prc-fuel">
          <div className="prc-fuel-fila">
            <span className="prc-fuel-icon" aria-hidden="true">⚡</span>
            <span className="prc-fuel-cifra">{plan.token_limit.toLocaleString('es-MX')}</span>
            {multiplicador && <span className="prc-fuel-mult">{multiplicador}</span>}
          </div>
          {/* «al mes» en un plan de dos días es una promesa que el producto no
              cumple: la prueba trae 350 y se acaba con ella. */}
          <div className="prc-fuel-texto">
            {plan.price === 0 ? 'de Energía Spacy para tu prueba' : 'de Energía Spacy al mes'}
          </div>
        </div>
      )}

      {/* Features con separadores */}
      <ul className="prc-lp-features">
        {caracteristicas.map((f, i) => {
          const isNo = f.startsWith('❌') || f.startsWith('No ');
          return (
            <li key={i} className={`prc-lp-feat ${isNo ? 'prc-lp-feat-no' : 'prc-lp-feat-yes'}`}>
              {/* Sin palomita verde: cada linea ya trae su propio icono en el
                  texto y dos iconos seguidos se peleaban entre si. La negativa
                  SI conserva su marca, porque ahi el simbolo es la informacion. */}
              {isNo && <span className="prc-lp-feat-icon" aria-hidden="true">✕</span>}
              <span>{f.replace(/^[✅❌]\s*/, '')}</span>
            </li>
          );
        })}
      </ul>

      {/* CTA bold uppercase */}
      <button
        className={`prc-lp-cta ${ctaCls}`}
        style={isFeatured && !isPopular && !isElite
          ? { background: planColor, color: '#000', border: 'none' }
          : undefined}
        onClick={() => plan.price > 0 && onSelect(plan)}
        disabled={plan.price === 0}
      >
        {plan.price === 0 ? (plan.cta_text || '✓ Tu plan actual') : (plan.cta_text || 'Empezar ahora')}
      </button>

    </div>
  );
}


// ─── Checkout Modal ──────────────────────────────────────────────────────────
function CheckoutModal({ plan, onClose, cupon = null }) {
  const [stage, setStage] = useState('options'); // 'options' | 'maintenance'
  const [closing, setClosing] = useState(false);
  const [yendo, setYendo] = useState(false);

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 300);
  };

  // Va por `enlaceWhatsApp` y no a mano. Dos motivos: el número estaba escrito
  // aquí ADEMÁS de en `contacto.js`, así que el día que cambie esta pantalla se
  // queda con el viejo; y ese helper es el que quita los emojis que WhatsApp
  // pinta como rombos rotos — Rob pidió que ninguno de nuestros mensajes salga
  // sin pasar por ahí.
  // ── CON CUPÓN, EL MENSAJE LO ESCRIBE EL SERVIDOR (20-09-2026) ──────────
  // Porque ese mensaje lleva el importe que gerencia va a cobrar. Si la cifra
  // la pusiera esta pantalla, cualquiera con las herramientas del navegador
  // abiertas podría pedir un 90% y el WhatsApp llegaría idéntico a uno bueno.
  // Aquí solo se abre el texto que el servidor ya selló, con su código.
  const handleWhatsApp = async (planName) => {
    if (cupon && plan?.id && plan.price > 0) {
      // La pestaña se abre ANTES de esperar al servidor: si se abre después,
      // el navegador lo trata como ventana emergente y la bloquea.
      const pestana = window.open('', '_blank', 'noopener');
      setYendo(true);
      try {
        const res = await api.post('/api/promociones/contratar', { plan_id: plan.id });
        const url = res.data?.enlace_whatsapp;
        if (url && pestana) { pestana.location = url; return; }
        if (url) { window.location.href = url; return; }
      } catch (_) {
        /* si el cupón ya no vale, se sigue por el camino de siempre */
      } finally {
        setYendo(false);
      }
      if (pestana) pestana.close();
    }
    const texto = `¡Hola! Quiero activar mi plan "${planName}" con transferencia SPEI. ¿Me puedes guiar?`;
    window.open(enlaceWhatsApp(texto), '_blank', 'noopener');
  };

  return (
    <div className={`prc-overlay ${closing ? 'prc-overlay-out' : ''}`} onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className={`prc-modal ${closing ? 'prc-modal-out' : ''}`} style={{ '--plan-color': plan.color || '#ffff00' }}>
        <button className="prc-modal-close" onClick={handleClose}>✕</button>

        {stage === 'options' ? (
          <>
            <div className="prc-modal-header">
              <div className="prc-modal-icon">🚀</div>
              <h3 className="prc-modal-title">¡Un paso para cambiar tu inglés!</h3>
              <p className="prc-modal-sub">Adquiriendo: <strong className="prc-modal-plan-name" style={{ color: plan.color || '#ffff00' }}>{plan.name}</strong></p>
            </div>

            <div className="prc-payment-grid">
              {/* ⭐ SPEI primero — la opción estrella */}
              <button className="prc-pay-btn prc-pay-spei" onClick={() => handleWhatsApp(plan.name)}>
                <span className="prc-pay-icon">🏦</span>
                <div>
                  <div className="prc-pay-label">Transferencia SPEI</div>
                  <div className="prc-pay-sub prc-pay-spei-sub">⚡ Activación en menos de 1 hora</div>
                </div>
                <span className="prc-pay-arrow">›</span>
              </button>

              <div className="prc-pay-divider"><span>— o elige otro método —</span></div>

              <button className="prc-pay-btn prc-pay-stripe" onClick={() => setStage('maintenance')}>
                <span className="prc-pay-icon">💳</span>
                <div>
                  <div className="prc-pay-label">Stripe</div>
                  <div className="prc-pay-sub">Tarjeta de crédito / débito</div>
                </div>
                <span className="prc-pay-arrow">›</span>
              </button>

              <button className="prc-pay-btn prc-pay-mp" onClick={() => setStage('maintenance')}>
                <span className="prc-pay-icon">🔵</span>
                <div>
                  <div className="prc-pay-label">MercadoPago</div>
                  <div className="prc-pay-sub">Rápido y seguro</div>
                </div>
                <span className="prc-pay-arrow">›</span>
              </button>
            </div>

            <div className="prc-modal-trust">
              <span>🔒 Pago 100% seguro</span>
              <span>·</span>
              <span>🛡️ Sin cargos ocultos</span>
              <span>·</span>
              <span>✉️ Confirmación inmediata</span>
            </div>
          </>
        ) : (
          <div className="prc-maintenance">
            <div className="prc-maintenance-icon">⚡</div>
            <h4>¡Perfecto! Estás a un paso</h4>
            <p>
              Nuestros métodos de pago automáticos están temporalmente en mantenimiento
              mientras actualizamos la plataforma. Para garantizarte el acceso{' '}
              <strong>hoy mismo</strong>, te atendemos personalmente por WhatsApp
              en menos de 10 minutos.
            </p>
            <button className="prc-whatsapp-btn" onClick={() => handleWhatsApp(plan.name)}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
              Continuar por WhatsApp
            </button>
            <button className="prc-back-btn" onClick={() => setStage('options')}>← Volver a opciones de pago</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
const FALLBACK_PLANS = {
  subscriptions: [
    {
      id: 1, name: "Free Trial", price: 0, currency: "MXN", category: "subscription",
      emoji: "🌱", color: "#9ca3af", featured: false, cta_text: "Tu plan actual",
      features: [`${duracionDeLaPrueba()} de acceso completo`, "50 Tokens de IA", "Tutor básico", "Glosario interactivo"],
    },
    {
      id: 2, name: "Premium", price: 299, original_price: 499, currency: "MXN", category: "subscription",
      emoji: "💎", color: "#ffff00", featured: true, badge: "⚡ MÁS POPULAR", discount_active: true,
      cta_text: "Comenzar Premium",
      features: [
        "Tokens IA ilimitados en clases",
        "Tutor IA personalizado 24/7",
        "NeuraCinema + Documentales",
        "Mini Games desbloqueados",
        "Progreso con análisis IA",
        "Soporte prioritario",
      ],
    },
  ],
  packages: [
    {
      id: 3, name: "Jumpstart", price: 800, currency: "MXN", category: "package",
      emoji: "👨‍🏫", color: "#a855f7", featured: false, badge: "CLASES EN VIVO",
      cta_text: "Conocer Teacher",
      features: ["4 clases 1-a-1 al mes", "Feedback personalizado", "Horario flexible", "Acceso a Plataforma incluido"],
    }
  ],
  tokens: [
    {
      id: 4, name: "Pack 500 Tokens", price: 99, currency: "MXN", category: "token",
      emoji: "⚡", color: "#00FFFF", featured: false, badge: "IDEAL RECARGA",
      cta_text: "Comprar Tokens",
      features: ["500 Tokens para IA", "No expiran nunca", "Úsalos en el Simulador", "Transferibles al siguiente mes"],
    },
    {
      id: 5, name: "Pack 1500 Tokens", price: 249, currency: "MXN", category: "token",
      emoji: "🚀", color: "#f472b6", featured: true, badge: "MEJOR VALOR",
      cta_text: "Máximo Potencial",
      features: ["1,500 Tokens para IA", "No expiran nunca", "Úsalos en cualquier módulo", "Acceso prioritario al Tutor", "Bono: +200 tokens de regalo"],
    },
  ]
};

export default function PricingPage() {
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // ── EL CUPÓN DEL ALUMNO (20-09-2026) ───────────────────────────────────
  // Rob: «ok, cupón aplicado, 20%, y los números cambian, para que pueda ver
  // cuánto realmente es lo que va a pagar».
  //
  // Se aplica SOLO al llegar: el alumno viene de pulsar un botón en su chat,
  // no de copiar un código. Pedirle que lo escriba aquí es perder a la mitad
  // en el trayecto — y el cupón ya es suyo, no hay nada que validar.
  const [cupon, setCupon] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await api.get('/api/public/plans');
        if (res.data?.subscriptions?.length || res.data?.packages?.length) {
          setPlans(res.data);
        }
      } catch (_) { /* use fallback */ }
      finally { setLoading(false); }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        // Aplicar es lo que avisa a gerencia de que está mirando precios. Si
        // no hay sesión o no tiene cupón, esto contesta 401/404 y la página
        // sigue igual que siempre: sin cupón no cambia nada.
        const res = await api.post('/api/promociones/aplicar', {});
        if (vivo && res.data?.cupon) setCupon(res.data.cupon);
      } catch (_) {
        try {
          const res = await api.get('/api/promociones/mio');
          if (vivo && res.data?.cupon) setCupon(res.data.cupon);
        } catch (__) { /* sin cupón, sin ruido */ }
      }
    })();
    return () => { vivo = false; };
  }, []);

  // ── Una sola oferta a la vista ─────────────────────────────────────────
  // Habia un conmutador Plataforma / Con Teacher / Tokens. Se quita: la
  // estrategia cambio y partir la pagina en tres pestanas obligaba al alumno a
  // elegir una categoria ANTES de haber entendido nada, y escondia dos tercios
  // de la oferta detras de un clic que casi nadie da. Los teachers y la compra
  // de tokens extra se explican ahora en las preguntas frecuentes, que es
  // donde se van a buscar.
  const displayPlans = plans.subscriptions || [];

  // Referencia del multiplicador: el plan de pago inmediatamente anterior en
  // precio. Se calcula con los planes publicados, asi que si mañana cambias
  // precios o tokens en el CRM la comparacion se recoloca sola.
  const dePago = displayPlans
    .filter(p => p.price > 0 && p.token_limit > 0)
    .sort((a, b) => a.price - b.price);
  const tokensPrevios = {};
  dePago.forEach((p, i) => { if (i > 0) tokensPrevios[p.id] = dePago[i - 1].token_limit; });

  return (
    <>
      <div className="prc-root">
        <Header />
        <FloatingOrbs />

        {/* ── Hero ── */}
        <section className="prc-hero">
          <SocialProofTicker />
          {/* ── El gancho ────────────────────────────────────────────────────
              Antes decia "Inteligencia Artificial aplicada al ingles" e
              "Invierte en tu Futuro": suena a un curso de ingles mas, y de
              curso de ingles el alumno ya se ha comprado tres. La promesa
              ahora no es saber, es SOLTARSE: el que llega aqui normalmente ya
              conoce las reglas y aun asi se congela al hablar. */}
          <div className="prc-hero-eyebrow">⚡ El gimnasio para tu inglés</div>
          <h1 className="prc-hero-title">
            Entrena <span className="prc-hero-highlight">hoy</span>, habla <span className="prc-hero-highlight">hoy</span>.
          </h1>
          <p className="prc-hero-sub">
            ¿Conoces las reglas pero te congelas al hablar? Rompe esa barrera.
            El entrenamiento diario es la única forma de generar los reflejos y
            la memoria muscular para conversar sin miedo.
          </p>

        </section>

        {/* ── El cupón del alumno ─────────────────────────────────────────
            Rob: «ok, cupón aplicado, 20%, y los números cambian». Esto lo dice
            con todas las letras, con su código y su fecha, para que no tenga
            que fiarse de que las cifras de abajo ya lo llevan puesto. */}
        {cupon && (
          <div className="prc-cupon-banner" data-prueba="cupon-banner">
            <span className="prc-cupon-icon">🎁</span>
            <div>
              <div className="prc-cupon-title">
                Cupón aplicado: {Number(cupon.pct).toFixed(0)}% de descuento
              </div>
              <div className="prc-cupon-sub">
                {cupon.leyenda} · código <strong>{cupon.codigo}</strong>
              </div>
            </div>
            <span className="prc-cupon-dias">
              {cupon.dias_restantes === 0
                ? 'último día'
                : `${cupon.dias_restantes} ${cupon.dias_restantes === 1 ? 'día' : 'días'}`}
            </span>
          </div>
        )}

        {/* ── Grid ── */}
        <section className="prc-grid-section">
          {loading ? (
            <div className="prc-loading">
              <div className="prc-loading-ring" />
              <span>Cargando planes...</span>
            </div>
          ) : (
            <div className="prc-grid">
              {displayPlans.map(plan => (
                <PlanCard key={plan.id} plan={plan} onSelect={setSelectedPlan} tokensPrevio={tokensPrevios[plan.id] || 0} cupon={cupon} />
              ))}
            </div>
          )}
        </section>

        {/* ── Trust Bar ── */}
        <section className="prc-trust-bar">
          {['🔒 Pago seguro con SSL', '🛡️ Sin contratos forzosos', '⚡ Activación en minutos', '💬 Soporte en WhatsApp', '📊 +200 estudiantes activos'].map(t => (
            <div key={t} className="prc-trust-item">{t}</div>
          ))}
        </section>

        {/* ── FAQ ── */}
        <section className="prc-faq">
          <h2 className="prc-faq-title">Preguntas frecuentes</h2>
          <div className="prc-faq-grid">
            {[
              { q: "¿Puedo cancelar en cualquier momento?", a: "Sí. Sin contratos ni penalizaciones. Tu acceso continúa hasta el final del período pagado." },
              { q: "¿Cómo funciona el pago SPEI?", a: "Nos contactas por WhatsApp, te enviamos la CLABE y en menos de 1 hora activamos tu cuenta manualmente." },
              { q: "¿Qué es la Energía Spacy?", a: "Es tu combustible para entrenar con Spacy: cada conversación, cada corrección y cada ejercicio con IA gasta un poco. Tu plan trae energía nueva cada mes y esa no se acumula; la que compras aparte sí es tuya hasta que la gastes. La de la prueba gratuita son 350 y no se renuevan." },
              { q: "¿Qué es el Tutor IA personalizado?", a: "Un asistente que aprende tu estilo, detecta tus errores frecuentes y adapta cada sesión a tu nivel real." },
              { q: "¿Puedo tomar clases con un teacher?", a: "Sí. Contamos con teachers certificados y con experiencia real enseñando a hispanohablantes. Escríbenos por WhatsApp y armamos tu paquete de clases a la medida de tu nivel y tus horarios." },
              // Lo que decía —«puedes recargar cuando quieras y se suma al
              // instante»— no existe: hoy la recarga la hace gerencia, y los
              // minijuegos se generan con IA, así que esos sí esperan.
              { q: "¿Y si se me acaba la Energía Spacy del mes?", a: "No te quedas fuera: sigues teniendo el entrenamiento completo y el glosario. Para volver a usar lo que lleva IA puedes comprar una recarga desde tu panel —lo que no gastes pasa al mes siguiente— o subir de plan." },
            ].map(({ q, a }) => (
              <div key={q} className="prc-faq-item">
                <div className="prc-faq-q">{q}</div>
                <div className="prc-faq-a">{a}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* ── Checkout Modal ── */}
      {selectedPlan && (
        <CheckoutModal plan={selectedPlan} onClose={() => setSelectedPlan(null)} cupon={cupon} />
      )}
    </>
  );
}
